/* Durable on-device store for Digital Health Files.
   Uses IndexedDB so a saved DHF survives app restarts, SW-cache eviction and
   offline sessions — this is the "DocSite offline folder" backing the
   "saved on your phone" promise. Pure browser, no dependency. */

const DB_NAME = 'docsite-dhf';
const STORE = 'files';
const DB_VERSION = 1;

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') return reject(new Error('no-idb'));
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const dbi = req.result;
      if (!dbi.objectStoreNames.contains(STORE)) {
        dbi.createObjectStore(STORE, { keyPath: 'token' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function saveDhfLocal(token, payload) {
  if (!token || !payload) return false;
  try {
    const dbi = await openDb();
    await new Promise((resolve, reject) => {
      const tx = dbi.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put({ token, payload, saved_at: new Date().toISOString() });
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
    dbi.close();
    return true;
  } catch { return false; }
}

export async function getDhfLocal(token) {
  if (!token) return null;
  try {
    const dbi = await openDb();
    const rec = await new Promise((resolve, reject) => {
      const tx = dbi.transaction(STORE, 'readonly');
      const r = tx.objectStore(STORE).get(token);
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => reject(r.error);
    });
    dbi.close();
    return rec ? rec.payload : null;
  } catch { return null; }
}

export async function listLocalDhfs() {
  try {
    const dbi = await openDb();
    const rows = await new Promise((resolve, reject) => {
      const tx = dbi.transaction(STORE, 'readonly');
      const r = tx.objectStore(STORE).getAll();
      r.onsuccess = () => resolve(r.result || []);
      r.onerror = () => reject(r.error);
    });
    dbi.close();
    return rows;
  } catch { return []; }
}

/* Ask the browser to mark our storage as persistent so the OS won't evict the
   saved file under storage pressure — the closest a web app gets to "forever". */
export async function requestPersistentStorage() {
  try {
    if (navigator.storage && navigator.storage.persist) {
      return await navigator.storage.persist();
    }
  } catch { /* ignore */ }
  return false;
}

/* ───────────────────────────────────────────────────────────────────────
   Zero-trust encrypted offline cache.
   Clinical data is NEVER stored in plaintext for secured files. We derive an
   AES-GCM key from the patient's 6-digit Master PIN (PBKDF2) and encrypt the
   payload before writing to IndexedDB. Decrypting requires the PIN, so a
   shared / stolen device cache is useless without it.
   ─────────────────────────────────────────────────────────────────────── */
const SEC_STORE = 'secure_files';

function openSecDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') return reject(new Error('no-idb'));
    const req = indexedDB.open(DB_NAME, DB_VERSION + 1);
    req.onupgradeneeded = () => {
      const dbi = req.result;
      if (!dbi.objectStoreNames.contains(STORE)) dbi.createObjectStore(STORE, { keyPath: 'token' });
      if (!dbi.objectStoreNames.contains(SEC_STORE)) dbi.createObjectStore(SEC_STORE, { keyPath: 'token' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function deriveKey(pin, saltBytes) {
  const enc = new TextEncoder();
  const baseKey = await crypto.subtle.importKey('raw', enc.encode(pin), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: saltBytes, iterations: 200000, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

export async function saveDhfLocalSecure(token, payload, pin) {
  if (!token || !payload || !pin) return false;
  try {
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const key = await deriveKey(pin, salt);
    const data = new TextEncoder().encode(JSON.stringify(payload));
    const cipher = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, data);
    const dbi = await openSecDb();
    await new Promise((resolve, reject) => {
      const tx = dbi.transaction(SEC_STORE, 'readwrite');
      tx.objectStore(SEC_STORE).put({
        token,
        salt: Array.from(salt),
        iv: Array.from(iv),
        cipher: Array.from(new Uint8Array(cipher)),
        saved_at: new Date().toISOString(),
      });
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
    dbi.close();
    return true;
  } catch { return false; }
}

export async function hasSecureLocal(token) {
  try {
    const dbi = await openSecDb();
    const rec = await new Promise((resolve, reject) => {
      const tx = dbi.transaction(SEC_STORE, 'readonly');
      const r = tx.objectStore(SEC_STORE).get(token);
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => reject(r.error);
    });
    dbi.close();
    return !!rec;
  } catch { return false; }
}

export async function getDhfLocalSecure(token, pin) {
  if (!token || !pin) return null;
  try {
    const dbi = await openSecDb();
    const rec = await new Promise((resolve, reject) => {
      const tx = dbi.transaction(SEC_STORE, 'readonly');
      const r = tx.objectStore(SEC_STORE).get(token);
      r.onsuccess = () => resolve(r.result);
      r.onerror = () => reject(r.error);
    });
    dbi.close();
    if (!rec) return null;
    const key = await deriveKey(pin, new Uint8Array(rec.salt));
    const plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: new Uint8Array(rec.iv) }, key, new Uint8Array(rec.cipher),
    );
    return JSON.parse(new TextDecoder().decode(plain));
  } catch { return null; }   // wrong PIN → decrypt throws → null
}
